import { z } from "zod";
import { RESERVED_SLUGS } from "../constants/statuses.js";
/** 24-char hex Mongo ObjectId. */
export const objectIdSchema = z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, "Not a valid id");
/**
 * Org and counter slugs. Lowercase, digits and single hyphens only, so they are
 * safe in a URL path and unambiguous when read aloud over the phone.
 */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, "At least 2 characters")
    .max(48, "At most 48 characters")
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens only");
export const orgSlugSchema = slugSchema.refine((s) => !RESERVED_SLUGS.includes(s), "This name is reserved by the platform");
/**
 * Indian mobile number. Accepts 10 digits, or the same with a 91 / +91 prefix,
 * and always stores the canonical `+91XXXXXXXXXX`.
 *
 * The old app stored some numbers with a country code and some without, then
 * matched them with an unanchored regex. Normalising on the way in removes the
 * whole problem.
 */
export const phoneSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s\-()]/g, ""))
    .refine((v) => /^(?:\+?91)?[6-9]\d{9}$/.test(v), "Enter a valid 10-digit mobile number")
    .transform((v) => `+91${v.slice(-10)}`);
/**
 * Dial codes offered by the create-organization form.
 *
 * Scoped deliberately. `phoneSchema` above is the identity of every person in
 * the product (D-18) and India-only by design — karigar sign-in, WhatsApp
 * delivery and the printed QR poster all assume `+91XXXXXXXXXX`. Widening
 * THAT would mean reworking those flows, so only the org admin's number, which
 * none of them touch, accepts a country code for now.
 *
 * `digits` is the national number length, after the dial code.
 */
export const DIAL_CODES = [
    { iso: "IN", dial: "+91", label: "India", digits: [10] },
    { iso: "AE", dial: "+971", label: "United Arab Emirates", digits: [9] },
    { iso: "SA", dial: "+966", label: "Saudi Arabia", digits: [9] },
    { iso: "QA", dial: "+974", label: "Qatar", digits: [8] },
    { iso: "OM", dial: "+968", label: "Oman", digits: [8] },
    { iso: "KW", dial: "+965", label: "Kuwait", digits: [8] },
    { iso: "SG", dial: "+65", label: "Singapore", digits: [8] },
    { iso: "MY", dial: "+60", label: "Malaysia", digits: [9, 10] },
    { iso: "LK", dial: "+94", label: "Sri Lanka", digits: [9] },
    { iso: "NP", dial: "+977", label: "Nepal", digits: [10] },
    { iso: "BD", dial: "+880", label: "Bangladesh", digits: [10] },
    { iso: "GB", dial: "+44", label: "United Kingdom", digits: [10] },
    { iso: "US", dial: "+1", label: "United States / Canada", digits: [10] },
    { iso: "AU", dial: "+61", label: "Australia", digits: [9] },
    { iso: "DE", dial: "+49", label: "Germany", digits: [10, 11] },
    { iso: "FR", dial: "+33", label: "France", digits: [9] },
];
/** India stays the default everywhere this list is offered. */
export const DEFAULT_DIAL_CODE = "+91";
/**
 * A mobile number carrying its own country code, canonicalised to E.164.
 *
 * The longest dial code wins, so `+1` cannot swallow a number that belongs to
 * `+971`. India keeps its extra rule — a mobile there starts 6-9 — because
 * dropping it would let a landline through a field that must reach WhatsApp.
 */
export const intlPhoneSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s\-()]/g, ""))
    .superRefine((v, ctx) => {
    if (!v.startsWith("+")) {
        ctx.addIssue({ code: "custom", message: "Choose a country code." });
        return;
    }
    const match = [...DIAL_CODES]
        .sort((a, b) => b.dial.length - a.dial.length)
        .find((c) => v.startsWith(c.dial));
    if (!match) {
        ctx.addIssue({ code: "custom", message: "That country code is not supported yet." });
        return;
    }
    const national = v.slice(match.dial.length);
    if (!/^\d+$/.test(national)) {
        ctx.addIssue({ code: "custom", message: "Enter digits only." });
        return;
    }
    if (!match.digits.includes(national.length)) {
        const expected = match.digits.join(" or ");
        ctx.addIssue({
            code: "custom",
            message: `A ${match.label} mobile number is ${expected} digits.`,
        });
        return;
    }
    if (match.dial === "+91" && !/^[6-9]/.test(national)) {
        ctx.addIssue({ code: "custom", message: "An Indian mobile number starts with 6, 7, 8 or 9." });
    }
});
export const emailSchema = z.email("Enter a valid email address").trim().toLowerCase();
/**
 * Email is optional for every person in the app; the mobile number is what
 * identifies them (D-18). A blank field means "no email".
 */
export const optionalEmailSchema = z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? undefined : v), emailSchema.optional());
/**
 * An optional field that an EMPTY value clears.
 *
 * These are all "not given" on create and "remove what was there" on update,
 * and both arrive as a blank input. Folding blank to `undefined` — which is
 * what these did first — made the second case unreachable: a GST number typed
 * once could never be taken off again, because the update skipped the key.
 * So blank is a value here, and it is the empty string.
 */
const optionalText = (max) => z.string().trim().max(max).optional();
/**
 * 15 characters: 2 state code, 10 PAN, 1 entity number, 1 'Z', 1 checksum.
 * Optional — plenty of depots are not separately registered.
 */
export const gstNumberSchema = z
    .string()
    .trim()
    .toUpperCase()
    .refine((v) => v === "" || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(v), "Enter a valid 15-character GSTIN")
    .optional();
export const pincodeSchema = z
    .string()
    .trim()
    .refine((v) => v === "" || /^[1-9][0-9]{5}$/.test(v), "Enter a valid 6-digit PIN code")
    .optional();
/**
 * Where a place of business sits.
 *
 * One shape for depots and counters both: they are the same kind of thing to
 * anyone doing the paperwork, and two forms asking for an address in two
 * different ways is how the same shop ends up recorded twice.
 *
 * `district` and `pincode` are filled from India Post's directory rather than
 * typed, but stay writable — a lookup that guesses wrong must never be able to
 * stop someone saving.
 */
export const addressFields = {
    district: optionalText(100),
    pincode: pincodeSchema,
    village: optionalText(120),
    address: optionalText(300),
    gstNumber: gstNumberSchema,
};
/** Password rules for staff accounts. */
export const passwordSchema = z
    .string()
    .min(8, "At least 8 characters")
    .max(128, "At most 128 characters");
export const nameSchema = z.string().trim().min(2, "At least 2 characters").max(120);
/** Optional free text shown under a name — a product note, a document blurb. */
export const descriptionSchema = z.string().trim().max(300).optional();
export const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, "Use a 6-digit hex color, like #C54E1B")
    .transform((v) => v.toUpperCase());
export const paginationSchema = z.object({
    filters: z.string().max(3000).optional(),
    includeFilters: z.literal("1").optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    q: z.string().trim().max(120).optional(),
});
/** Build a slug from a display name. Collisions are resolved by the caller. */
export function slugify(input) {
    return input
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 48);
}
/** Last 10 digits — the only form we ever compare phone numbers on. */
export function phoneLast10(phone) {
    return phone.replace(/\D/g, "").slice(-10);
}
/** "" means "leave it as it is" — an edit form sending an empty field for an account that has none yet. */
const blankToUndefined = (v) => (typeof v === "string" && v.trim() === "" ? undefined : v);
/** For edits only: a blank mobile is ignored, a filled one must be valid (D-18). */
export const optionalPhoneSchema = z.preprocess(blankToUndefined, phoneSchema.optional());
/** An optional id from a query string, where "" means "not chosen". */
export const optionalIdSchema = z.preprocess(blankToUndefined, objectIdSchema.optional());
//# sourceMappingURL=common.js.map